import {
  MAX_FEATURED_CATEGORIES,
  MAX_HOME_SLIDES,
  type Category,
  type HomeSlide,
  type StoreHome,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  Cell,
  CheckboxField,
  Loading,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import { categoriesQuery, homeQuery, keys, tenantApi } from '../api/tenant';
import { CrudSection, submitWith } from '../components/CrudSection';
import {
  FormStatus,
  ImageUploadField,
  mergeErrors,
  NumberField,
  Thumbnail,
} from '../components/fields';
import { ActiveBadge } from '../components/status';
import { StaleNotice, useVersionedDraft } from '../components/versioned';
import { fill, he } from '../i18n/he';
import { categoryPath, categoryTree } from '../lib/categories';
import {
  homeDraft,
  homeRequest,
  slideCreateRequest,
  slideDraft,
  slideUpdateRequest,
  type SlideDraft,
} from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';
import { moveItem, nextPosition } from '../lib/lists';

export function HomePage() {
  const home = useQuery(homeQuery());
  const categories = useQuery(categoriesQuery());
  const canWrite = useCan('store.settings.write');
  return (
    <>
      <PageHeader title={he.home.title} />
      {home.isPending ? <Loading /> : null}
      {home.isError ? <Alert>{errorMessage(home.error)}</Alert> : null}
      {home.data === undefined ? null : (
        <div className="flex flex-col gap-6">
          <HomeForm
            home={home.data}
            categories={categories.data?.items ?? []}
            categoriesLoaded={categories.isSuccess}
            canWrite={canWrite}
          />
          <Slides slides={home.data.slides} canWrite={canWrite} />
        </div>
      )}
    </>
  );
}

/** The welcome banner, the newest-products row and the featured categories (in order). */
function HomeForm({
  home,
  categories,
  categoriesLoaded,
  canWrite,
}: {
  home: StoreHome;
  categories: readonly Category[];
  categoriesLoaded: boolean;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const editor = useVersionedDraft(home, (value) => value.version, homeDraft);
  const { draft, set } = editor;
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const save = useMutation({
    mutationFn: tenantApi.updateHome,
    onSuccess: (updated) => {
      editor.reset(updated);
      queryClient.setQueryData(keys.home(), updated);
    },
  });
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...homeQuery(), staleTime: 0 })
      // A failed read shows on the page itself (the home query's error).
      .then(editor.reset, () => undefined);
  };
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    // Categories deleted meanwhile are dropped (once the list is known).
    const existing = categoriesLoaded ? new Set(categories.map((category) => category.id)) : null;
    const parsed = homeRequest(draft, editor.baseVersion, existing);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };
  const errors = mergeErrors(localErrors, save.error);

  return (
    <form onSubmit={onSubmit} noValidate data-testid="home-form">
      <fieldset disabled={!canWrite} className="flex flex-col gap-6">
        {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}
        <StaleNotice stale={editor.stale} onReload={reload} />
        <Card title={he.home.hero}>
          <div className="flex flex-col gap-4">
            <p className="text-xs text-slate-500">{he.home.heroHint}</p>
            <TextField
              label={he.home.heroEyebrow}
              name="heroEyebrow"
              maxLength={80}
              value={draft.heroEyebrow}
              error={errors['hero.eyebrow']}
              onChange={(event) => {
                set({ heroEyebrow: event.target.value });
              }}
            />
            <TextField
              label={he.home.heroTitle}
              name="heroTitle"
              maxLength={120}
              value={draft.heroTitle}
              error={errors['hero.title']}
              onChange={(event) => {
                set({ heroTitle: event.target.value });
              }}
            />
            <TextField
              label={he.home.heroHighlight}
              name="heroHighlight"
              maxLength={80}
              value={draft.heroHighlight}
              error={errors['hero.highlight']}
              onChange={(event) => {
                set({ heroHighlight: event.target.value });
              }}
            />
            <TextAreaField
              label={he.home.heroText}
              name="heroText"
              rows={3}
              maxLength={500}
              value={draft.heroText}
              error={errors['hero.text']}
              onChange={(event) => {
                set({ heroText: event.target.value });
              }}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={he.home.heroButtonLabel}
                name="heroButtonLabel"
                maxLength={40}
                value={draft.heroButtonLabel}
                error={errors['hero.buttonLabel']}
                onChange={(event) => {
                  set({ heroButtonLabel: event.target.value });
                }}
              />
              <TextField
                label={he.home.heroButtonLink}
                name="heroButtonLink"
                dir="ltr"
                maxLength={300}
                hint={he.popups.linkHint}
                value={draft.heroButtonLink}
                error={errors['hero.buttonLink']}
                onChange={(event) => {
                  set({ heroButtonLink: event.target.value });
                }}
              />
            </div>
            <CheckboxField
              label={he.home.showNewest}
              checked={draft.showNewest}
              onChange={(event) => {
                set({ showNewest: event.target.checked });
              }}
            />
          </div>
        </Card>

        <Card title={he.home.featured}>
          <FeaturedCategories
            value={draft.featuredCategoryIds}
            categories={categories}
            categoriesLoaded={categoriesLoaded}
            error={errors['featuredCategoryIds']}
            onChange={(featuredCategoryIds) => {
              set({ featuredCategoryIds });
            }}
          />
        </Card>

        <FormStatus
          error={save.error}
          localInvalid={Object.keys(localErrors).length > 0}
          saved={save.isSuccess}
          onReload={reload}
        />
        {canWrite ? (
          <div>
            <Button type="submit" busy={save.isPending}>
              {he.common.save}
            </Button>
          </div>
        ) : null}
      </fieldset>
    </form>
  );
}

/** An ordered pick of categories: add from a list, move up and down, remove. */
function FeaturedCategories({
  value,
  categories,
  categoriesLoaded,
  error,
  onChange,
}: {
  value: readonly string[];
  categories: readonly Category[];
  categoriesLoaded: boolean;
  error: string | undefined;
  onChange: (ids: string[]) => void;
}) {
  const [choice, setChoice] = useState('');
  const listId = useId();
  const full = value.length >= MAX_FEATURED_CATEGORIES;
  const available = categoryTree(categories).filter((node) => !value.includes(node.category.id));
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">
        {fill(he.home.featuredHint, { max: MAX_FEATURED_CATEGORIES })}
      </p>
      {value.length === 0 ? (
        <p className="text-sm text-slate-500">{he.home.noFeatured}</p>
      ) : (
        <ol id={listId} className="flex flex-col gap-1 text-sm">
          {value.map((id, index) => (
            <li
              key={id}
              data-testid="featured-category"
              className="flex items-center justify-between gap-2 rounded px-2 py-1 ring-1 ring-slate-200"
            >
              <span>
                {index + 1}.{' '}
                {categoryPath(categories, id) ||
                  (categoriesLoaded ? (
                    // Deleted since it was chosen: dropped on the next save.
                    <span className="text-amber-700" data-testid="featured-missing">
                      {he.home.missingCategory}
                    </span>
                  ) : (
                    he.common.none
                  ))}
              </span>
              <span className="flex gap-1">
                <Button
                  tone="secondary"
                  aria-label={`${he.common.moveUp} ${String(index + 1)}`}
                  disabled={index === 0}
                  onClick={() => {
                    onChange(moveItem(value, index, -1));
                  }}
                >
                  ↑
                </Button>
                <Button
                  tone="secondary"
                  aria-label={`${he.common.moveDown} ${String(index + 1)}`}
                  disabled={index === value.length - 1}
                  onClick={() => {
                    onChange(moveItem(value, index, 1));
                  }}
                >
                  ↓
                </Button>
                <Button
                  tone="secondary"
                  onClick={() => {
                    onChange(value.filter((entry) => entry !== id));
                  }}
                >
                  {he.common.remove}
                </Button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {full ? (
        <p className="text-xs text-slate-500">{he.home.featuredFull}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <SelectField
            label={he.home.chooseCategory}
            name="featuredChoice"
            value={choice}
            onChange={(event) => {
              setChoice(event.target.value);
            }}
          >
            <option value="">{he.common.none}</option>
            {available.map(({ category, depth }) => (
              <option key={category.id} value={category.id}>
                {`${'— '.repeat(depth)}${category.name}`}
              </option>
            ))}
          </SelectField>
          <Button
            tone="secondary"
            disabled={choice === ''}
            onClick={() => {
              onChange([...value, choice]);
              setChoice('');
            }}
          >
            {he.home.addCategory}
          </Button>
        </div>
      )}
      {error === undefined ? null : (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** The slider: each slide is its own entity (created, replaced and deleted on its own). */
function Slides({ slides, canWrite }: { slides: readonly HomeSlide[]; canWrite: boolean }) {
  return (
    <CrudSection<HomeSlide, SlideDraft>
      title={he.home.slides}
      intro={fill(he.home.slidesHint, { max: MAX_HOME_SLIDES })}
      entity="slide"
      createLabel={he.home.createSlide}
      newTitle={he.home.newSlide}
      editTitle={he.home.editSlide}
      emptyText={he.home.noSlides}
      items={slides}
      loading={false}
      loadError={null}
      invalidate={[keys.home()]}
      canWrite={canWrite}
      maxItems={MAX_HOME_SLIDES}
      fullText={he.home.slidesFull}
      createConflict={he.home.slidesFull}
      head={[
        he.home.slideImage,
        he.home.slideTitle,
        he.home.slideLink,
        he.common.position,
        he.common.status,
      ]}
      rowData={(slide) => ({ 'data-title': slide.title ?? '' })}
      cells={(slide) => (
        <>
          <Cell>
            <Thumbnail file={slide.image.file} alt={slide.title ?? he.home.slideImage} size={56} />
          </Cell>
          <Cell>{slide.title ?? he.common.none}</Cell>
          <Cell className="ltr text-xs">{slide.link ?? he.common.none}</Cell>
          <Cell>{slide.position}</Cell>
          <Cell>
            <ActiveBadge active={slide.isActive} />
          </Cell>
        </>
      )}
      draftOf={(slide) => slideDraft(slide, nextPosition(slides))}
      submit={(draft, slide) =>
        slide === undefined
          ? submitWith(slideCreateRequest(draft), tenantApi.createSlide)
          : submitWith(slideUpdateRequest(draft, slide.version), (body) =>
              tenantApi.updateSlide(slide.id, body),
            )
      }
      remove={(slide) => tenantApi.deleteSlide(slide.id)}
      renderForm={({ draft, set, errors }) => (
        <>
          <ImageUploadField
            label={he.home.slideImage}
            value={draft.image}
            error={errors['mediaId']}
            testId="slide-image"
            onChange={(image) => {
              set({ image });
            }}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label={he.home.slideTitle}
              name="title"
              maxLength={120}
              value={draft.title}
              error={errors['title']}
              onChange={(event) => {
                set({ title: event.target.value });
              }}
            />
            <TextField
              label={he.home.slideButton}
              name="buttonLabel"
              maxLength={40}
              value={draft.buttonLabel}
              error={errors['buttonLabel']}
              onChange={(event) => {
                set({ buttonLabel: event.target.value });
              }}
            />
            <TextField
              label={he.home.slideLink}
              name="link"
              dir="ltr"
              maxLength={300}
              hint={he.popups.linkHint}
              value={draft.link}
              error={errors['link']}
              onChange={(event) => {
                set({ link: event.target.value });
              }}
            />
            <NumberField
              label={he.common.position}
              name="position"
              value={draft.position}
              error={errors['position']}
              onChange={(position) => {
                set({ position });
              }}
            />
          </div>
          <TextAreaField
            label={he.home.slideText}
            name="text"
            rows={2}
            maxLength={300}
            value={draft.text}
            error={errors['text']}
            onChange={(event) => {
              set({ text: event.target.value });
            }}
          />
          <CheckboxField
            label={he.home.slideActive}
            checked={draft.isActive}
            onChange={(event) => {
              set({ isActive: event.target.checked });
            }}
          />
        </>
      )}
    />
  );
}
